import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Utensils, Loader2, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { getBackendUrl } from '../utils/config';

const LoginPage = () => {
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    email: '', 
    password: '', 
    name: '', 
    role: 'medewerker' 
  });
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [needsActivation, setNeedsActivation] = useState(false);
  const [resendingActivation, setResendingActivation] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error('Vul alle velden in');
      return;
    }
    setLoading(true);
    setNeedsActivation(false);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Welkom terug!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Inloggen mislukt';
      if (error.response?.status === 403 && detail.includes('niet geactiveerd')) {
        setNeedsActivation(true);
        setRegisteredEmail(loginData.email);
        toast.error('Account nog niet geactiveerd');
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.email || !registerData.password || !registerData.name) {
      toast.error('Vul alle velden in');
      return;
    }
    setLoading(true);
    try {
      const result = await register(registerData.email, registerData.password, registerData.name, registerData.role);
      setRegistrationSuccess(true);
      setRegisteredEmail(registerData.email);
      if (result.email_sent) {
        toast.success('Activatie email verzonden!');
      } else {
        toast.success('Account aangemaakt! Controleer je email.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registratie mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleResendActivation = async () => {
    if (!registeredEmail) return;
    setResendingActivation(true);
    try {
      const api = axios.create({ baseURL: `${getBackendUrl()}/api` });
      await api.post('/auth/resend-activation', { email: registeredEmail });
      toast.success('Nieuwe activatielink verzonden!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon email niet verzenden');
    } finally {
      setResendingActivation(false);
    }
  };

  // Show activation required message
  if (needsActivation) {
    return (
      <div className="login-container" data-testid="login-page">
        <div className="login-image">
          <img 
            src="https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="Restaurant"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
        <div className="login-form">
          <div className="w-full max-w-md">
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="text-center">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-amber-600" />
                </div>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Account Activeren</CardTitle>
                <CardDescription>
                  Je account is nog niet geactiveerd. Controleer je email ({registeredEmail}) voor de activatielink.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  onClick={handleResendActivation}
                  disabled={resendingActivation}
                  className="w-full"
                  variant="outline"
                >
                  {resendingActivation ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verzenden...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Nieuwe activatielink versturen
                    </>
                  )}
                </Button>
                <Button 
                  onClick={() => setNeedsActivation(false)}
                  variant="ghost"
                  className="w-full"
                >
                  Terug naar inloggen
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Show registration success message
  if (registrationSuccess) {
    return (
      <div className="login-container" data-testid="login-page">
        <div className="login-image">
          <img 
            src="https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="Restaurant"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
        <div className="login-form">
          <div className="w-full max-w-md">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardHeader className="text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Controleer je email</CardTitle>
                <CardDescription>
                  We hebben een activatielink gestuurd naar <strong>{registeredEmail}</strong>. 
                  Klik op de link in de email om je account te activeren.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-stone-600 bg-white p-4 rounded-lg border border-stone-200">
                  <p className="font-medium mb-2">Geen email ontvangen?</p>
                  <ul className="list-disc list-inside space-y-1 text-stone-500">
                    <li>Controleer je spam/ongewenste map</li>
                    <li>Wacht een paar minuten</li>
                    <li>Controleer of je email adres correct is</li>
                  </ul>
                </div>
                <Button 
                  onClick={handleResendActivation}
                  disabled={resendingActivation}
                  className="w-full"
                  variant="outline"
                >
                  {resendingActivation ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verzenden...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Opnieuw versturen
                    </>
                  )}
                </Button>
                <Button 
                  onClick={() => setRegistrationSuccess(false)}
                  variant="ghost"
                  className="w-full"
                >
                  Terug naar inloggen
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container" data-testid="login-page">
      {/* Left side - Image */}
      <div className="login-image">
        <img 
          src="https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Restaurant"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            Het Horeca Dashboard
          </h1>
          <p className="text-white/80 text-lg">
            Realtime inzicht, sturing en structuur voor uw horecabedrijf
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="login-form">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#C2410C] flex items-center justify-center">
              <Utensils className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
                Horeca Dashboard
              </h2>
              <p className="text-sm text-[#57534E]">Management systeem</p>
            </div>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Inloggen</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Registreren</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card className="border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Welkom terug</CardTitle>
                  <CardDescription>Log in op uw account</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="naam@bedrijf.nl"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="input-base"
                        data-testid="login-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Wachtwoord</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="input-base"
                        data-testid="login-password-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full btn-primary"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Bezig...
                        </>
                      ) : (
                        'Inloggen'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card className="border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Account aanmaken</CardTitle>
                  <CardDescription>Maak een nieuw account aan</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Naam</Label>
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Uw naam"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        className="input-base"
                        data-testid="register-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="naam@bedrijf.nl"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="input-base"
                        data-testid="register-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Wachtwoord</Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="input-base"
                        data-testid="register-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-role">Rol</Label>
                      <Select
                        value={registerData.role}
                        onValueChange={(value) => setRegisterData({ ...registerData, role: value })}
                      >
                        <SelectTrigger className="input-base" data-testid="register-role-select">
                          <SelectValue placeholder="Selecteer rol" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="company">Bedrijfseigenaar</SelectItem>
                          <SelectItem value="medewerker">Medewerker</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full btn-primary"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Bezig...
                        </>
                      ) : (
                        'Registreren'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
